// client/src/pages/secure/EscrowLedgerPage.js — one escrow ledger, 68 IAC 1450.750(i)(2).
//
// Replaces TransactionLedgerPage, which asked for a ledger by TRANSACTION number from
// an endpoint that had since become keyed by LEDGER number, and read the reply from a
// `data` field this app's API client never returns — so it could not show a ledger.
// A ledger is its own record now (a lease or an owner's managed funds has one too),
// and it must name every party, so the parties are shown and can be completed here.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';
import { printLedgerCard } from '../../utils/escrowPrint';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const KIND = { sales_transaction: 'Sales transaction', lease: 'Lease', client: 'Managed funds', broker_funds: 'Broker funds', other: 'Other' };
const FUNDS = { earnest_money: 'Earnest money', security_deposit: 'Security deposit', rent: 'Rent', broker_funds: 'Broker funds', other: 'Other' };
const ROLES = ['buyer', 'seller', 'tenant', 'landlord', 'owner', 'guarantor', 'cooperating_broker', 'attorney', 'other'];

const AddPartyModal = ({ isOpen, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [role, setRole] = useState('buyer');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => { if (isOpen) { setName(''); setRole('buyer'); setError(''); } }, [isOpen]);
    if (!isOpen) return null;
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError('');
        try { await onSave({ party_name: name.trim(), party_role: role }); onClose(); }
        catch (err) { setError(err.message || 'Could not add the party.'); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
                <h2 className="text-xl font-bold text-slate-800 mb-4">Add a party</h2>
                <form onSubmit={submit} className="space-y-4">
                    <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name or company" required className="p-2 border rounded w-full" />
                    <select value={role} onChange={e => setRole(e.target.value)} className="p-2 border rounded w-full">
                        {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                    </select>
                    {error && <p className="text-red-600 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={busy || !name.trim()} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">{busy ? 'Saving…' : 'Add party'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const EscrowLedgerPage = () => {
    const { ledgerId } = useParams();
    const apiClient = useApiClient();
    const user = getCurrentUser();
    const canEdit = !!(user?.isManagingBroker || ['Bookkeeper', 'Office Manager'].includes(user?.staffRole));

    const [ledger, setLedger] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [addingParty, setAddingParty] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            setLedger(await apiClient.get(`/api/escrow/ledgers/${ledgerId}`));
            setError('');
        } catch (err) {
            setError(err.message || 'Could not load the ledger.');
        } finally {
            setIsLoading(false);
        }
    }, [ledgerId, apiClient]);

    useEffect(() => { load(); }, [load]);

    const print = async () => {
        try { printLedgerCard(await apiClient.get(`/api/escrow/ledgers/${ledgerId}/card`)); }
        catch (err) { setError(err.message || 'Could not build the ledger card.'); }
    };

    const addParty = async (party) => {
        await apiClient.post(`/api/escrow/ledgers/${ledgerId}/parties`, party);
        setNotice('Party added.');
        load();
    };

    const removeParty = async (p) => {
        if (!window.confirm(`Remove ${p.party_name} from this ledger?`)) return;
        try {
            await apiClient.delete(`/api/escrow/ledgers/parties/${p.party_id}`);
            setNotice('Party removed.');
            load();
        } catch (err) { setError(err.message); }
    };

    const close = async () => {
        if (!window.confirm('Close this ledger? It can only close at a zero balance, and its entries are kept five years.')) return;
        try {
            const r = await apiClient.post(`/api/escrow/ledgers/${ledgerId}/close`, {});
            setNotice(r?.message || 'Ledger closed.');
            load();
        } catch (err) { setError(err.message); }
    };

    if (isLoading) return <div className="p-8">Loading ledger…</div>;
    if (!ledger) return <div className="p-8 text-red-600">{error || 'Ledger not found.'}</div>;

    const autoOpened = (ledger.notes || '').startsWith('Opened automatically');

    return (
        <div className="p-4 md:p-8 space-y-6">
            <AddPartyModal isOpen={addingParty} onClose={() => setAddingParty(false)} onSave={addParty} />
            <Link to="/dashboard/escrow?tab=ledgers" className="text-indigo-600 hover:underline inline-block">&larr; Back to Escrow</Link>

            <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Escrow ledger #{ledger.ledger_id} · {KIND[ledger.ledger_kind] || ledger.ledger_kind}</p>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-800">{ledger.description}</h1>
                        {ledger.property_address && ledger.property_address !== ledger.description &&
                            <p className="text-slate-600">{ledger.property_address}</p>}
                        <p className="text-sm text-slate-500 mt-1">
                            {ledger.account_name} · {ledger.bank_name} · {FUNDS[ledger.funds_type] || ledger.funds_type}
                            {ledger.reference ? ` · ${ledger.reference}` : ''}
                        </p>
                        <p className="text-sm text-slate-500">Opened {ledger.opened_on}{ledger.closed_on ? ` · closed ${ledger.closed_on}` : ''}</p>
                    </div>
                    <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${ledger.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-700'}`}>{ledger.status}</span>
                        <p className="text-2xl font-bold text-slate-800 mt-2">{formatCurrency(ledger.balance)}</p>
                        <p className="text-xs text-slate-500">balance on this ledger</p>
                        <div className="flex gap-2 justify-end mt-3">
                            <button onClick={print} className="px-3 py-2 text-sm bg-slate-800 text-white rounded hover:bg-slate-700">Print ledger card</button>
                            {canEdit && ledger.status === 'open' &&
                                <button onClick={close} className="px-3 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300">Close ledger</button>}
                        </div>
                    </div>
                </div>

                {notice && <div className="p-3 mb-4 rounded bg-green-50 text-green-800 text-sm">{notice}</div>}
                {error && <div className="p-3 mb-4 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-semibold text-slate-800">Parties</h2>
                        {canEdit && <button onClick={() => setAddingParty(true)} className="text-sm text-indigo-600 hover:underline">+ Add party</button>}
                    </div>
                    {autoOpened && (
                        <p className="text-sm bg-amber-50 text-amber-800 p-3 rounded mb-2">
                            This ledger was opened automatically when an entry was posted, and no parties were on
                            record. The rule requires the names of all parties — add them.
                        </p>
                    )}
                    <ul className="divide-y border rounded">
                        {(ledger.parties || []).map(p => (
                            <li key={p.party_id} className="flex justify-between items-center px-4 py-2 text-sm">
                                <span><span className="font-medium text-slate-800">{p.party_name}</span> <span className="text-slate-500">— {String(p.party_role).replace('_', ' ')}</span></span>
                                {canEdit && (ledger.parties || []).length > 1 &&
                                    <button onClick={() => removeParty(p)} className="text-red-600 hover:underline text-xs">Remove</button>}
                            </li>
                        ))}
                    </ul>
                </div>

                <h2 className="text-lg font-semibold text-slate-800 mb-2">Entries</h2>
                <DataList rows={ledger.entries || []} rowKey="entry_id" csvName={`escrow-ledger-${ledger.ledger_id || ledgerId}`}
                    defaultSort={{ key: 'entry_date', dir: 'asc' }} empty="No entries on this ledger yet."
                    note="Oldest first, so the running balance reads down the page."
                    filters={[{ key: 'entry_type', label: 'Type' }]}
                    columns={[
                        { key: 'entry_date', label: 'Date', render: (e) => <span className="whitespace-nowrap">{e.entry_date}</span> },
                        { key: 'payee_or_payor', label: 'Received from / paid to' },
                        { key: 'ref', label: 'Check # / memo', value: (e) => (e.check_number ? `Check #${e.check_number}` : (e.memo || '')), render: (e) => <span className="text-slate-500">{e.check_number ? `Check #${e.check_number}` : (e.memo || '')}</span> },
                        { key: 'entry_type', label: 'Type' },
                        { key: 'received', label: 'Received', type: 'number', value: (e) => (e.entry_type === 'Deposit' ? e.amount : null), render: (e) => <span className="text-green-700">{e.entry_type === 'Deposit' ? formatCurrency(e.amount) : ''}</span> },
                        { key: 'disbursed', label: 'Disbursed', type: 'number', value: (e) => (e.entry_type !== 'Deposit' ? e.amount : null), render: (e) => <span className="text-red-700">{e.entry_type !== 'Deposit' ? formatCurrency(e.amount) : ''}</span> },
                        { key: 'ledger_balance', label: 'Balance', type: 'number', render: (e) => <span className="font-semibold">{formatCurrency(e.ledger_balance)}</span> },
                    ]} />
                <p className="text-xs text-slate-500 mt-4">{ledger.authority}</p>
            </div>
        </div>
    );
};

export default EscrowLedgerPage;
